import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { initials, colorFor } from "@/lib/avatar";
import { isAtRisk } from "@/components/plan/dueDateStatus";

// Layout constants — slightly tighter than the per-project Gantt so we can
// fit more days in view at once.
const BASE_DAY_WIDTH = 26;
const ROW_HEIGHT = 44;
const ROW_GAP = 6;
const HEADER_HEIGHT = 48;
const LEFT_GUTTER = 220;
const PERSON_BAR_HEIGHT = 18;
const LANE_GAP = 4;             // vertical gap between stacked overlapping bars
const DRAG_THRESHOLD = 5;       // px before mousedown becomes a drag

const parseISO = (s) => {
  if (!s) return null;
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(Date.UTC(y, m - 1, d));
};
const addDays = (date, n) => {
  const d = new Date(date.getTime());
  d.setUTCDate(d.getUTCDate() + n);
  return d;
};
const isoOf = (d) => d.toISOString().slice(0, 10);
const daysBetween = (a, b) => Math.round((b - a) / 86400000);
const fmtShort = (d) => d.toLocaleDateString("en-US", { day: "numeric", month: "short", timeZone: "UTC" });
const fmtMonth = (d) => d.toLocaleDateString("en-US", { month: "long", year: "numeric", timeZone: "UTC" });
const weekday = (d) => d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" }).slice(0, 1);

// Build month spans for the header band (e.g. "March" stretches across its days)
function monthSpans(start, end, dayWidth) {
  const spans = [];
  let cur = new Date(start.getTime());
  while (cur <= end) {
    const monthStart = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth(), 1));
    const monthEnd = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 0));
    const visibleStart = monthStart < start ? start : monthStart;
    const visibleEnd = monthEnd > end ? end : monthEnd;
    const x = daysBetween(start, visibleStart) * dayWidth;
    const w = (daysBetween(visibleStart, visibleEnd) + 1) * dayWidth;
    spans.push({ label: fmtMonth(visibleStart), x, w });
    cur = addDays(monthEnd, 1);
  }
  return spans;
}

export default function StudioGantt({
  people = [],
  tasks = [],
  conflicts = [],
  rangeStart,
  rangeEnd,
  capacity = 8,
  allocations = {},
  onRebook = null,
  zoom = 1,
}) {
  const navigate = useNavigate();
  const [dragState, setDragState] = useState(null);
  const dragStateRef = useRef(null);

  // Effective day cell width based on zoom. All x/width math reads from this.
  const DAY_WIDTH = Math.max(8, Math.round(BASE_DAY_WIDTH * (zoom || 1)));

  // Mirror state to ref so window listeners always see the latest values
  // without re-binding on every render.
  useEffect(() => { dragStateRef.current = dragState; }, [dragState]);

  const dates = useMemo(() => {
    const s = parseISO(rangeStart);
    const e = parseISO(rangeEnd);
    if (!s || !e) return { start: null, end: null, days: [], totalDays: 0 };
    const days = [];
    let cur = s;
    while (cur <= e) {
      days.push(new Date(cur.getTime()));
      cur = addDays(cur, 1);
    }
    return { start: s, end: e, days, totalDays: days.length };
  }, [rangeStart, rangeEnd]);

  // Group tasks by assignee
  const tasksByUser = useMemo(() => {
    const m = new Map();
    for (const t of tasks) {
      if (!t.assigned_user_id) continue;
      if (!m.has(t.assigned_user_id)) m.set(t.assigned_user_id, []);
      m.get(t.assigned_user_id).push(t);
    }
    return m;
  }, [tasks]);

  // Lane-pack overlapping tasks per person so two tasks that share a date
  // don't draw on top of each other. Each task gets a lane index; rows with
  // multiple lanes get taller so every bar is visible.
  const layout = useMemo(() => {
    const rows = people.map((person) => {
      const personTasks = (tasksByUser.get(person.user_id) || []).slice();
      // Sort by start, then end — needed for the greedy lane assignment below.
      personTasks.sort((a, b) => {
        const sa = a.start || "";
        const sb = b.start || "";
        if (sa !== sb) return sa < sb ? -1 : 1;
        const ea = a.end || a.start || "";
        const eb = b.end || b.start || "";
        return ea < eb ? -1 : ea > eb ? 1 : 0;
      });
      // Greedy lane assignment: place each task in the first lane whose
      // current last-end is strictly before this task's start.
      const lanes = []; // each lane = { endIso }
      const taskLanes = new Map(); // task.id → lane index
      for (const t of personTasks) {
        const ts = t.start || "";
        const te = t.end || t.start || "";
        let placed = false;
        for (let i = 0; i < lanes.length; i++) {
          if (lanes[i].endIso < ts) {
            lanes[i].endIso = te;
            taskLanes.set(t.id, i);
            placed = true;
            break;
          }
        }
        if (!placed) {
          lanes.push({ endIso: te });
          taskLanes.set(t.id, lanes.length - 1);
        }
      }
      const laneCount = Math.max(1, lanes.length);
      // Count tasks that are currently late or due in next 48h (and not done).
      // Matches the predicate used by the Launchpad At-Risk widget.
      let atRiskCount = 0;
      for (const t of personTasks) {
        if (isAtRisk(t)) atRiskCount += 1;
      }
      return { person, personTasks, taskLanes, laneCount, atRiskCount };
    });
    // Per-row absolute Y + height (so overlap-heavy rows can grow)
    const rowYs = [];
    const rowHeights = [];
    let cy = HEADER_HEIGHT;
    for (const r of rows) {
      rowYs.push(cy);
      const computedH =
        r.laneCount <= 1
          ? ROW_HEIGHT
          : Math.max(ROW_HEIGHT, r.laneCount * (PERSON_BAR_HEIGHT + LANE_GAP) + 8);
      rowHeights.push(computedH);
      cy += computedH + ROW_GAP;
    }
    return { rows, rowYs, rowHeights, bodyHeight: cy - HEADER_HEIGHT };
  }, [people, tasksByUser]);

  // Group conflicts by user for fast lookup
  const conflictsByUserDay = useMemo(() => {
    const m = new Map();
    for (const c of conflicts) {
      const key = `${c.user_id}|${c.iso_date}`;
      m.set(key, c);
    }
    return m;
  }, [conflicts]);

  if (!dates.start || !dates.end) {
    return (
      <div className="text-center py-16 text-zinc-500 text-sm">
        Invalid date range
      </div>
    );
  }

  // ── Drag-to-rebook handlers (Studio polish) ──────────────────────────
  // mousedown on a bar → start tracking. If movement exceeds DRAG_THRESHOLD,
  // we're in drag mode and clicks suppress. mouseup commits or treats as
  // click depending on whether drag mode was entered.
  const startDrag = (e, task, rowIdx) => {
    if (!onRebook || e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const initial = {
      taskId: task.id,
      task,
      rowIdx,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      dayDelta: 0,
      rowDelta: 0,
      dragging: false,
    };
    setDragState(initial);
    dragStateRef.current = initial;

    const onMove = (mv) => {
      const ds = dragStateRef.current;
      if (!ds) return;
      const dx = mv.clientX - ds.startMouseX;
      const dy = mv.clientY - ds.startMouseY;
      if (!ds.dragging && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
        return;
      }
      const dayDelta = Math.round(dx / DAY_WIDTH);
      const rowDelta = Math.round(dy / (ROW_HEIGHT + ROW_GAP));
      const next = { ...ds, dragging: true, dayDelta, rowDelta };
      dragStateRef.current = next;
      setDragState(next);
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      const ds = dragStateRef.current;
      dragStateRef.current = null;
      setDragState(null);
      if (!ds) return;
      if (!ds.dragging || (ds.dayDelta === 0 && ds.rowDelta === 0)) {
        // Treat as click — open project plan
        navigate(`/projects/${ds.task.project_id}/plan`);
        return;
      }
      // Compute new start (calendar day shift)
      let newStart = ds.task.start;
      if (ds.dayDelta !== 0) {
        const d = parseISO(ds.task.start);
        if (d) {
          newStart = isoOf(addDays(d, ds.dayDelta));
        }
      }
      // Compute new assignee from row position
      let newUserId = ds.task.assigned_user_id;
      if (ds.rowDelta !== 0) {
        const newRowIdx = ds.rowIdx + ds.rowDelta;
        if (newRowIdx >= 0 && newRowIdx < people.length) {
          newUserId = people[newRowIdx].user_id;
        }
      }
      const changes = {};
      if (newStart !== ds.task.start) changes.start = newStart;
      if (newUserId !== ds.task.assigned_user_id) changes.assigned_user_id = newUserId;
      if (Object.keys(changes).length > 0) {
        onRebook(ds.task, changes);
      }
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  if (people.length === 0) {
    return (
      <div
        data-testid="studio-empty-state"
        className="border border-dashed border-zinc-800 rounded-md p-16 text-center"
      >
        <div className="text-zinc-500 text-sm">
          No assigned tasks fall inside this window.
        </div>
      </div>
    );
  }

  const totalWidth = LEFT_GUTTER + dates.totalDays * DAY_WIDTH;
  const totalHeight = HEADER_HEIGHT + layout.bodyHeight + 16;
  const months = monthSpans(dates.start, dates.end, DAY_WIDTH);
  const todayIso = isoOf(new Date(Date.now() - new Date().getTimezoneOffset() * 60000));
  const todayIdx = dates.days.findIndex((d) => isoOf(d) === todayIso);

  return (
    <div
      className="border border-zinc-800 rounded-md bg-zinc-950/60 overflow-auto"
      data-testid="studio-gantt"
      style={{ maxHeight: "calc(100vh - 320px)" }}
    >
      <svg
        width={totalWidth}
        height={totalHeight}
        className="block"
        style={{ minWidth: "100%" }}
      >
        {/* ── Header band: months ─────────────────────────────────── */}
        <g>
          {months.map((m, i) => (
            <g key={`mo-${i}`}>
              <rect
                x={LEFT_GUTTER + m.x}
                y={0}
                width={m.w}
                height={22}
                fill="#0a0a0a"
              />
              <text
                x={LEFT_GUTTER + m.x + 8}
                y={15}
                className="font-mono"
                fill="#71717a"
                fontSize={10}
                style={{ letterSpacing: "0.1em", textTransform: "uppercase" }}
              >
                {m.label}
              </text>
            </g>
          ))}
        </g>
        {/* Day cells header */}
        <g transform={`translate(0, 22)`}>
          {dates.days.map((d, i) => {
            const dow = d.getUTCDay();
            const isWeekend = dow === 0 || dow === 6;
            const x = LEFT_GUTTER + i * DAY_WIDTH;
            return (
              <g key={`d-${i}`}>
                <rect
                  x={x}
                  y={0}
                  width={DAY_WIDTH}
                  height={26}
                  fill={isWeekend ? "#0a0a0a" : "#09090b"}
                />
                <text
                  x={x + DAY_WIDTH / 2}
                  y={11}
                  fill={isWeekend ? "#52525b" : "#a1a1aa"}
                  fontSize={9}
                  textAnchor="middle"
                  className="font-mono"
                >
                  {weekday(d)}
                </text>
                <text
                  x={x + DAY_WIDTH / 2}
                  y={22}
                  fill={isWeekend ? "#52525b" : "#e4e4e7"}
                  fontSize={10}
                  textAnchor="middle"
                  className="font-mono"
                >
                  {d.getUTCDate()}
                </text>
              </g>
            );
          })}
        </g>

        {/* Header border */}
        <line
          x1={0}
          x2={totalWidth}
          y1={HEADER_HEIGHT - 0.5}
          y2={HEADER_HEIGHT - 0.5}
          stroke="#27272a"
        />

        {/* Day dividers — vertical hairlines between every day so weekdays
            visually separate. Monday boundaries get a slightly stronger
            stroke to break up multi-week ranges. Skip index 0 (left edge
            already implied by the gutter). */}
        <g data-testid="studio-day-dividers">
          {dates.days.map((d, i) => {
            if (i === 0) return null;
            const isMonday = d.getUTCDay() === 1;
            const x = LEFT_GUTTER + i * DAY_WIDTH;
            return (
              <line
                key={`div-${i}`}
                x1={x}
                x2={x}
                y1={HEADER_HEIGHT}
                y2={totalHeight}
                stroke={isMonday ? "#3f3f46" : "#1f1f22"}
                strokeWidth={isMonday ? 1 : 1}
              />
            );
          })}
        </g>

        {/* Today vertical line */}
        {todayIdx >= 0 && (
          <line
            x1={LEFT_GUTTER + todayIdx * DAY_WIDTH + DAY_WIDTH / 2}
            x2={LEFT_GUTTER + todayIdx * DAY_WIDTH + DAY_WIDTH / 2}
            y1={HEADER_HEIGHT}
            y2={totalHeight}
            stroke="#f59e0b"
            strokeOpacity={0.35}
            strokeDasharray="3 3"
          />
        )}

        {/* ── Body rows ──────────────────────────────────────────── */}
        {layout.rows.map((row, rowIdx) => {
          const person = row.person;
          const rowY = layout.rowYs[rowIdx];
          const rowH = layout.rowHeights[rowIdx];
          const personTasks = row.personTasks;
          const laneCount = row.laneCount;

          return (
            <g key={person.user_id} data-testid={`studio-row-${person.user_id}`}>
              {/* Row background */}
              <rect
                x={0}
                y={rowY}
                width={totalWidth}
                height={rowH}
                fill={rowIdx % 2 === 0 ? "#0a0a0a" : "transparent"}
              />

              {/* Weekend stripes within the row */}
              {dates.days.map((d, i) => {
                const dow = d.getUTCDay();
                if (dow !== 0 && dow !== 6) return null;
                return (
                  <rect
                    key={`we-${rowIdx}-${i}`}
                    x={LEFT_GUTTER + i * DAY_WIDTH}
                    y={rowY}
                    width={DAY_WIDTH}
                    height={rowH}
                    fill="#000"
                    fillOpacity={0.25}
                  />
                );
              })}

              {/* Conflict cell highlights (background, behind bars) */}
              {dates.days.map((d, i) => {
                const iso = isoOf(d);
                const c = conflictsByUserDay.get(`${person.user_id}|${iso}`);
                if (!c) return null;
                return (
                  <rect
                    key={`cf-${rowIdx}-${i}`}
                    x={LEFT_GUTTER + i * DAY_WIDTH + 1}
                    y={rowY + 1}
                    width={DAY_WIDTH - 2}
                    height={rowH - 2}
                    fill="#f43f5e"
                    fillOpacity={0.12}
                    stroke="#f43f5e"
                    strokeOpacity={0.55}
                    strokeWidth={1}
                    rx={2}
                  />
                );
              })}

              {/* Left gutter — avatar + name */}
              <g>
                <rect x={0} y={rowY} width={LEFT_GUTTER} height={rowH} fill="#09090b" />
                <circle
                  cx={20}
                  cy={rowY + rowH / 2}
                  r={13}
                  fill={colorFor(person.user_id)}
                />
                <text
                  x={20}
                  y={rowY + rowH / 2 + 4}
                  fill="#0a0a0a"
                  fontSize={10}
                  fontWeight={700}
                  textAnchor="middle"
                  className="font-mono"
                >
                  {initials(person.name || person.email)}
                </text>
                <text
                  x={42}
                  y={rowY + rowH / 2 - 2}
                  fill="#e4e4e7"
                  fontSize={12}
                  className="font-medium"
                >
                  {(person.name || person.email).slice(0, 22)}
                </text>
                <text
                  x={42}
                  y={rowY + rowH / 2 + 12}
                  fill={row.atRiskCount > 0 ? "#fb923c" : "#71717a"}
                  fontSize={10}
                  className="font-mono"
                  style={{ letterSpacing: "0.05em" }}
                >
                  {personTasks.length} task{personTasks.length === 1 ? "" : "s"}
                  {laneCount > 1 ? ` · ${laneCount} lanes` : ""}
                  {row.atRiskCount > 0 ? ` · ${row.atRiskCount} at risk` : ""}
                </text>
              </g>
              <line
                x1={LEFT_GUTTER - 0.5}
                x2={LEFT_GUTTER - 0.5}
                y1={rowY}
                y2={rowY + rowH}
                stroke="#27272a"
              />

              {/* Render bars (one per task, colored by project) */}
              {personTasks.map((task) => {
                const ts = parseISO(task.start);
                const te = parseISO(task.end || task.start);
                if (!ts || !te) return null;
                // Clip to visible window
                const visStart = ts < dates.start ? dates.start : ts;
                const visEnd = te > dates.end ? dates.end : te;
                if (visEnd < dates.start || visStart > dates.end) return null;
                const startIdx = daysBetween(dates.start, visStart);
                const span = daysBetween(visStart, visEnd) + 1;
                // Base day-precision x/w
                let x = LEFT_GUTTER + startIdx * DAY_WIDTH + 2;
                let w = Math.max(span * DAY_WIDTH - 4, 8);
                // Phase-B hour-precision: when a task has explicit start_time /
                // end_time (opt-in walker output), nudge the leading/trailing
                // edges by the fraction-of-day the clock time represents.
                // We assume a visual 09:00-17:00 clock window (8h span) — this
                // matches typical Whiplash projects; users on the 6h/day
                // config still see relative accuracy since their times fall
                // inside 9-16.
                const CLOCK_START_MIN = 9 * 60;   // 09:00
                const CLOCK_END_MIN   = 17 * 60;  // 17:00
                const CLOCK_SPAN_MIN  = CLOCK_END_MIN - CLOCK_START_MIN; // 480 min
                const parseHHMM = (v) => {
                  if (!v || typeof v !== "string" || !v.includes(":")) return null;
                  const [h, m] = v.split(":").map(Number);
                  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
                  return h * 60 + m;
                };
                if (task.start_time && ts >= dates.start) {
                  const sm = parseHHMM(task.start_time);
                  if (sm !== null) {
                    const fracIntoDay = Math.min(1, Math.max(0, (sm - CLOCK_START_MIN) / CLOCK_SPAN_MIN));
                    const shift = fracIntoDay * (DAY_WIDTH - 2);
                    x = LEFT_GUTTER + startIdx * DAY_WIDTH + 2 + shift;
                    w = Math.max(w - shift, 6);
                  }
                }
                if (task.end_time && te <= dates.end) {
                  const em = parseHHMM(task.end_time);
                  if (em !== null) {
                    const fracRemaining = Math.min(1, Math.max(0, (CLOCK_END_MIN - em) / CLOCK_SPAN_MIN));
                    const trim = fracRemaining * (DAY_WIDTH - 2);
                    w = Math.max(w - trim, 6);
                  }
                }
                const laneIdx = row.taskLanes.get(task.id) || 0;
                const y =
                  laneCount <= 1
                    ? rowY + (rowH - PERSON_BAR_HEIGHT) / 2
                    : rowY + 6 + laneIdx * (PERSON_BAR_HEIGHT + LANE_GAP);
                const color = task.project_color?.bar || "#71717a";
                const text = task.project_color?.text || "#fafafa";
                // Sub-day duration tasks get a visible "h" marker so it's
                // obvious why the bar is narrow.
                const durationDays = Number(task.duration) || 1;
                const isSubDay = durationDays > 0 && durationDays < 1;
                const isDone = task.kanban_status === "done";

                return (
                  <g
                    key={task.id}
                    data-testid={`studio-task-${task.id}`}
                    style={{ cursor: onRebook ? "grab" : "pointer" }}
                    onMouseDown={onRebook ? (e) => startDrag(e, task, rowIdx) : undefined}
                    onClick={
                      onRebook
                        ? undefined
                        : () => navigate(`/projects/${task.project_id}/plan`)
                    }
                  >
                    <title>{`${task.name} · ${task.project_name} · ${task.start}${isDone ? " · DONE" : ""}${isSubDay ? ` · ${Math.round(durationDays * 8)}h` : ""}${onRebook ? " · drag to rebook · click to open" : ""}`}</title>
                    <rect
                      x={x}
                      y={y}
                      width={w}
                      height={PERSON_BAR_HEIGHT}
                      fill={color}
                      fillOpacity={
                        dragState?.taskId === task.id && dragState?.dragging
                          ? 0.35
                          : isDone
                          ? 0.35
                          : 0.9
                      }
                      stroke={isSubDay ? "#0a0a0a" : "none"}
                      strokeWidth={isSubDay ? 1 : 0}
                      rx={3}
                    />
                    {/* Phase pip on the leading edge */}
                    <rect
                      x={x}
                      y={y}
                      width={3}
                      height={PERSON_BAR_HEIGHT}
                      fill={text}
                      fillOpacity={isDone ? 0.45 : 0.85}
                      rx={1}
                    />
                    {/* Completed-task strike-through — horizontal line across the bar. */}
                    {isDone && (
                      <line
                        data-testid={`studio-task-strike-${task.id}`}
                        x1={x + 2}
                        x2={x + w - 2}
                        y1={y + PERSON_BAR_HEIGHT / 2}
                        y2={y + PERSON_BAR_HEIGHT / 2}
                        stroke="#fafafa"
                        strokeOpacity={0.85}
                        strokeWidth={1.5}
                      />
                    )}
                    {w > 50 && (
                      <text
                        x={x + 8}
                        y={y + PERSON_BAR_HEIGHT / 2 + 4}
                        fill="#0a0a0a"
                        fontSize={10}
                        fontWeight={600}
                        fillOpacity={isDone ? 0.55 : 1}
                        textDecoration={isDone ? "line-through" : "none"}
                        className="font-mono"
                      >
                        {task.name.length > Math.floor(w / 7) ? task.name.slice(0, Math.floor(w / 7) - 1) + "…" : task.name}
                      </text>
                    )}
                    {isSubDay && w <= 50 && (
                      <text
                        x={x + w / 2}
                        y={y + PERSON_BAR_HEIGHT / 2 + 4}
                        fill="#0a0a0a"
                        fontSize={9}
                        fontWeight={700}
                        textAnchor="middle"
                        fillOpacity={isDone ? 0.55 : 1}
                        textDecoration={isDone ? "line-through" : "none"}
                        className="font-mono"
                      >
                        {`${Math.round(durationDays * 8)}h`}
                      </text>
                    )}
                  </g>
                );
              })}

              {/* Row separator */}
              <line
                x1={0}
                x2={totalWidth}
                y1={rowY + rowH + ROW_GAP / 2}
                y2={rowY + rowH + ROW_GAP / 2}
                stroke="#18181b"
              />
            </g>
          );
        })}

        {/* Drag-ghost overlay — proposed new position for the bar in flight. */}
        {dragState?.dragging && (() => {
          const t = dragState.task;
          const proposedRowIdx = Math.max(0, Math.min(people.length - 1, dragState.rowIdx + dragState.rowDelta));
          const ts = parseISO(t.start);
          const te = parseISO(t.end || t.start);
          if (!ts || !te) return null;
          const proposedStart = addDays(ts, dragState.dayDelta);
          const proposedEnd = addDays(te, dragState.dayDelta);
          const visStart = proposedStart < dates.start ? dates.start : proposedStart;
          const visEnd = proposedEnd > dates.end ? dates.end : proposedEnd;
          if (visEnd < dates.start || visStart > dates.end) return null;
          const startIdx = daysBetween(dates.start, visStart);
          const span = daysBetween(visStart, visEnd) + 1;
          const x = LEFT_GUTTER + startIdx * DAY_WIDTH + 2;
          const w = Math.max(span * DAY_WIDTH - 4, 8);
          const rowY = layout.rowYs[proposedRowIdx] ?? HEADER_HEIGHT;
          const rowH = layout.rowHeights[proposedRowIdx] ?? ROW_HEIGHT;
          const y = rowY + (rowH - PERSON_BAR_HEIGHT) / 2;
          const color = t.project_color?.bar || "#f59e0b";
          const newPerson = people[proposedRowIdx];
          return (
            <g pointerEvents="none" data-testid="studio-drag-ghost">
              <rect
                x={x}
                y={y}
                width={w}
                height={PERSON_BAR_HEIGHT}
                fill={color}
                fillOpacity={0.5}
                stroke="#fafafa"
                strokeWidth={1.5}
                strokeDasharray="3 2"
                rx={3}
              />
              <rect
                x={x}
                y={y - 22}
                width={Math.max(w, 180)}
                height={18}
                fill="#09090b"
                stroke="#f59e0b"
                strokeOpacity={0.7}
                rx={2}
              />
              <text
                x={x + 6}
                y={y - 9}
                fill="#f59e0b"
                fontSize={10}
                className="font-mono"
              >
                {isoOf(proposedStart)}
                {dragState.rowDelta !== 0 && newPerson ? ` → ${(newPerson.name || newPerson.email).slice(0, 18)}` : ""}
              </text>
            </g>
          );
        })()}
      </svg>
    </div>
  );
}

// Sidecar component: conflict feed pinned to the right side of the page.
export function StudioConflictFeed({ conflicts, peopleById, onResolve }) {
  if (!conflicts || conflicts.length === 0) {
    return (
      <div className="rounded-md border border-emerald-900/40 bg-emerald-950/20 p-5">
        <div className="text-overline text-emerald-400/80 mb-2">All clear</div>
        <p className="text-xs text-zinc-400">
          No one is over-allocated across projects in this window.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-rose-900/50 bg-rose-950/20" data-testid="studio-conflict-feed">
      <div className="px-4 py-3 border-b border-rose-900/40 flex items-center gap-2">
        <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
        <span className="text-overline text-rose-400">Cross-Project Conflicts · {conflicts.length}</span>
      </div>
      <ul className="divide-y divide-rose-900/30 max-h-[480px] overflow-auto">
        {conflicts.map((c) => {
          const person = peopleById[c.user_id];
          return (
            <li
              key={`${c.user_id}-${c.iso_date}`}
              data-testid={`studio-conflict-${c.user_id}-${c.iso_date}`}
              className="px-4 py-3"
            >
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <div className="text-sm font-medium text-zinc-100 truncate">
                  {person?.name || person?.email || c.user_id}
                </div>
                <div className="font-mono text-[10px] text-rose-300">
                  {c.total_hours}h / {c.capacity}h
                </div>
              </div>
              <div className="text-xs text-zinc-500 font-mono mb-2">{c.iso_date}</div>
              <ul className="space-y-1">
                {c.projects.map((p) => (
                  <li
                    key={p.project_id}
                    className="flex items-center justify-between text-xs"
                  >
                    <button
                      type="button"
                      data-testid={`studio-resolve-${c.user_id}-${c.iso_date}-${p.project_id}`}
                      onClick={() => onResolve?.(p.project_id)}
                      className="flex items-center gap-1.5 text-zinc-300 hover:text-amber-500 transition-colors truncate"
                      title="Open project plan"
                    >
                      <span className="truncate">{p.project_name}</span>
                      <ExternalLink className="w-2.5 h-2.5" />
                    </button>
                    <span className="font-mono text-zinc-500 ml-2">{p.hours}h</span>
                  </li>
                ))}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
